// originally from https://github.com/marmelab/react-admin/blob/4865cb699c72f15cc32c8808ba1b4848ff1f086f/packages/ra-ui-materialui/src/auth/LoginForm.tsx#L10
// then modified

import type { LoginMethod } from "@Common/types/better_auth";
import { Button, CardContent, CircularProgress } from "@mui/material";
import {
	type ComponentsOverrides,
	type SxProps,
	styled,
	type Theme,
	useThemeProps,
} from "@mui/material/styles";
import { Form, required, useLogin, useNotify, useTranslate } from "ra-core";
import * as React from "react";
import { TextInput, type TranslateFunction } from "react-admin";
import { match } from "ts-pattern";

const ActualForm = (props: {
	context: LoginMethod;
	translate: TranslateFunction;
}) => {
	const { context, translate } = props;
	return match(context)
		.with("magic_link", () => {
			return (
				<TextInput
					autoFocus
					source="username"
					label={translate("ra.auth.username")}
					autoComplete="username"
					validate={required()}
				/>
			);
		})
		.with("sso", () => null)
		.exhaustive();
};

export const LoginForm = (
	inProps: LoginFormProps & { context: LoginMethod },
) => {
	const props = useThemeProps({
		props: inProps,
		name: PREFIX,
	});
	const { context } = inProps;
	const { redirectTo, className, sx } = props;
	const [loading, setLoading] = React.useState(false);
	const login = useLogin();
	const translate = useTranslate();
	const notify = useNotify();

	const submit = (values: FormData) => {
		setLoading(true);
		const loginPayload = match(context)
			.with("sso", () => {
				return { context };
			})
			.with("magic_link", () => {
				return { context, ...values };
			})
			.exhaustive();

		login(loginPayload, redirectTo)
			.then(() => {
				setLoading(false);
				const notifMsg = match(context)
					.with("sso", () => {
						return "Redirecting...";
					})
					.with("magic_link", () => {
						return "Email with authentication link sent";
					})
					.exhaustive();
				notify(notifMsg);
			})
			.catch((error) => {
				setLoading(false);
				notify(
					typeof error === "string"
						? error
						: typeof error === "undefined" || !error.message
							? "ra.auth.sign_in_error"
							: error.message,
					{
						type: "error",
						messageArgs: {
							_:
								typeof error === "string"
									? error
									: error?.message
										? error.message
										: undefined,
						},
					},
				);
			});
	};

	return (
		<StyledForm
			// @ts-expect-error — issue is there in original RA code
			onSubmit={submit}
			mode="onChange"
			noValidate
			className={className ?? ""}
			sx={sx ?? null}
		>
			<CardContent className={LoginFormClasses.content}>
				<ActualForm context={context} translate={translate} />
				<Button
					variant="contained"
					type="submit"
					color="primary"
					disabled={loading}
					fullWidth
					className={LoginFormClasses.button}
				>
					{loading ? (
						<CircularProgress
							className={LoginFormClasses.icon}
							size={19}
							thickness={3}
						/>
					) : (
						translate("ra.auth.sign_in")
					)}
				</Button>
			</CardContent>
		</StyledForm>
	);
};

const PREFIX = "RaLoginForm";

export const LoginFormClasses = {
	content: `${PREFIX}-content`,
	button: `${PREFIX}-button`,
	icon: `${PREFIX}-icon`,
};

const StyledForm = styled(Form, {
	name: PREFIX,
	overridesResolver: (_props, styles) => styles["root"],
})(({ theme }) => ({
	[`& .${LoginFormClasses.content}`]: {
		width: 300,
		paddingBottom: `${theme.spacing(2)}!important`,
	},
	[`& .${LoginFormClasses.button}`]: {
		marginTop: theme.spacing(2),
	},
	[`& .${LoginFormClasses.icon}`]: {
		margin: theme.spacing(0.3),
	},
}));

interface FormData {
	username: string;
	password: string;
}
export interface LoginFormProps {
	redirectTo?: string;
	className?: string;
	sx?: SxProps<Theme>;
	children?: React.ReactNode;
}

declare module "@mui/material/styles" {
	interface ComponentNameToClassKey {
		RaLoginForm: "root" | "content" | "button" | "icon";
	}

	interface ComponentsPropsList {
		RaLoginForm: Partial<LoginFormProps>;
	}

	interface Components {
		RaLoginForm?: {
			defaultProps?: ComponentsPropsList["RaLoginForm"];
			styleOverrides?: ComponentsOverrides<
				Omit<Theme, "components">
			>["RaLoginForm"];
		};
	}
}
